import audioContext from '../Components/AudioContext'
import generateId from '../Utility/generateId'

enum AudioNodePlaybackState {
    initial = 'initial',
    started = 'started',
    paused = 'paused',
    stopped = 'stopped',
}

export default class AudioNode {
    id: string
    private src: string
    private node: AudioBufferSourceNode
    private gain: GainNode
    private playbackState: AudioNodePlaybackState = AudioNodePlaybackState.initial

    constructor(src: string) {
        this.id = generateId(src)
        this.src = src
        this.node = audioContext.createBufferSource()
        this.gain = audioContext.createGain()
        this.node.connect(this.gain)
    }

    load = async () => {
        // only load if buffer doesn't exist
        if (this.node.buffer === null) {
            // download
            try {
                const response = await fetch(this.src, { mode: 'no-cors' })

                if (response.ok) {
                    const buffer = await response.arrayBuffer()
                    // decode buffer -- wrapped in Promise for Safari interop
                    this.node.buffer = await new Promise((resolve) => audioContext.decodeAudioData(buffer, resolve))
                }
            } catch (e) {
                console.warn('Inamove::AudioNode => fetch source failed with error:', e)
            }
        }

        return
    }

    hasBuffer = (): boolean => this.node.buffer !== null

    play = (offset = 0, onendedCallback: () => void) => {
        this.playbackState = AudioNodePlaybackState.started
        this.node.start(0, offset)
        this.node.onended = () => {
            this.playbackState = AudioNodePlaybackState.stopped
            this.refreshBuffer()
            onendedCallback()
        }
    }

    stop = () => {
        try {
            this.node.stop()
        } catch (e) {
            console.warn('Inamove:AudioNode => tried to stop audio that has not been started yet.')
        }
        // WHY: no Event needed for 'onended'
        // @ts-ignore
        this.node.onended && this.node.onended()
    }

    loop = (offset?: number) => {
        if (this.playbackState === AudioNodePlaybackState.initial) {
            this.node.loop = true
            // Why: those offsets allow for smoother loop transition
            this.node.loopStart = 0.1
            this.node.loopEnd = this.node.buffer ? this.node.buffer.duration - 0.1 : 0

            this.node.start(0, offset)
            this.playbackState = AudioNodePlaybackState.started

            this.node.onended = () => {
                this.playbackState = AudioNodePlaybackState.stopped
                this.refreshBuffer()
            }
        }
    }

    pause = () => {
        if (this.playbackState !== AudioNodePlaybackState.started) {
            return
        }

        // WHY: We can not easily pause and resume AudioBufferSourceNodes so we use this hack.
        // @see https://github.com/WebAudio/web-audio-api-v2/issues/105#issuecomment-724089679
        this.node.playbackRate.setValueAtTime(0, 0)
        this.playbackState = AudioNodePlaybackState.paused
    }

    resume = () => {
        if (this.playbackState === AudioNodePlaybackState.paused) {
            // WHY: see this.pause()
            this.node.playbackRate.setValueAtTime(1, 0)
            this.playbackState = AudioNodePlaybackState.started
        }
    }

    connect = (destination: GainNode) => {
        this.gain.connect(destination)
    }

    disconnect = () => {
        this.gain.disconnect()
    }

    refreshBuffer = () => {
        // Why: AudioBufferSourceNodes can only be played once so clone it to make it playable again
        this.node.disconnect()
        const nodeCopy = audioContext.createBufferSource()
        nodeCopy.buffer = this.node.buffer
        nodeCopy.connect(this.gain)
        this.node = nodeCopy
        this.playbackState = AudioNodePlaybackState.initial
    }

    setMute = (mute: boolean) => {
        this.gain.gain.setValueAtTime(mute ? 0 : 1, 0)
    }
}
